import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sun, Moon, Menu, X } from 'lucide-react';

interface NavbarProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

export default function Navbar({ theme, toggleTheme }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<string>('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [logoImgError, setLogoImgError] = useState<boolean>(false);
  const [cartCount, setCartCount] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      return parseInt(localStorage.getItem('mbj-cart-count') || '0', 10);
    }
    return 0;
  });

  // Monitor cart count changes from Store
  useEffect(() => {
    const handleCartUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ count: number }>;
      setCartCount(customEvent.detail?.count || 0);
    };

    window.addEventListener('mbj-cart-updated', handleCartUpdate);
    return () => window.removeEventListener('mbj-cart-updated', handleCartUpdate);
  }, []);

  // Monitor Scroll Position
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Monitor active scroll section with robust viewport coverage spy
  useEffect(() => {
    const handleScrollActiveSection = () => {
      const isAtTop = window.scrollY < 100;
      if (isAtTop) {
        setActiveSection('home');
        return;
      }

      const isAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 120;
      if (isAtBottom) {
        setActiveSection('contact');
        return;
      }

      const sections = ['home', 'about', 'store', 'services', 'work', 'testimonials', 'contact'];
      const viewportHeight = window.innerHeight;
      
      let currentActive = 'home';
      let maxIntersectionHeight = 0;

      sections.forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          // Calculate intersection heights beautifully and accurately
          const activeTop = Math.max(rect.top, 0);
          const activeBottom = Math.min(rect.bottom, viewportHeight);
          const visibleHeight = Math.max(0, activeBottom - activeTop);

          if (visibleHeight > maxIntersectionHeight) {
            maxIntersectionHeight = visibleHeight;
            currentActive = id;
          }
        }
      });

      if (currentActive) {
        setActiveSection(currentActive);
      }
    };

    window.addEventListener('scroll', handleScrollActiveSection, { passive: true });
    // Run immediately to set initial active section
    handleScrollActiveSection();

    return () => window.removeEventListener('scroll', handleScrollActiveSection);
  }, []);

  const navItems = [
    { label: 'ABOUT', href: '#about', id: 'about' },
    { label: 'STORE', href: '#store', id: 'store' },
    { label: 'SERVICES', href: '#services', id: 'services' },
    { label: 'WORK', href: '#work', id: 'work' },
    { label: 'TESTIMONIALS', href: '#testimonials', id: 'testimonials' },
    ...(cartCount > 0 ? [{ label: 'BAG', href: '#store', id: 'bag' }] : []),
    { label: 'CONNECT', href: '#contact', id: 'contact' },
  ];

  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    
    // 1. Resolve correct target section element
    const actualTargetId = targetId === 'bag' ? 'store' : targetId;
    const element = document.getElementById(actualTargetId);
    
    // 2. Collapse the mobile menu smoothly right away to stabilize document heights
    setMobileMenuOpen(false);

    if (element) {
      // Use client rect position with window scrollY for maximum iframe/mobile compatibility
      const rect = element.getBoundingClientRect();
      const elemTop = rect.top + window.scrollY;
      
      // Exact offset calculation to account for fixed navbar height and safe padding
      const navbarHeight = isScrolled ? 72 : 84;
      const scrollOffset = elemTop - navbarHeight;

      // Allow micro-delay for the menu collapse transition to begin before scrolling
      setTimeout(() => {
        try {
          window.scrollTo({
            top: scrollOffset,
            behavior: 'smooth',
          });
        } catch (err) {
          // Fallback scrollIntoView if native scrollTo behavior fails
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 50);
    }

    // 3. For the cart ('bag') entry, trigger sliding panel activation
    if (targetId === 'bag') {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('mbj-open-cart'));
      }, 350);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-40 transition-all duration-300 ${
        isScrolled
          ? 'py-4 bg-white/70 dark:bg-neutral-950/70 backdrop-blur-md border-b border-neutral-200/40 dark:border-neutral-800/40'
          : 'py-6 bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Brand Logo - supports fallback to gorgeous typographic text logo */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center gap-3 font-display tracking-tight text-neutral-900 dark:text-white"
          data-cursor="hover"
        >
          {!logoImgError ? (
            <img
              src="/assets/logo.png"
              alt="MBJ - Made By Jahangir Logo"
              onError={() => setLogoImgError(true)}
              className="h-5 max-h-[20px] w-auto object-contain transition-all duration-300"
            />
          ) : (
            <div className="flex items-center gap-2">
              <span className="font-syne text-sm font-extrabold tracking-tight text-emerald-500">MBJ</span>
              <span className="font-mono text-[8px] font-bold uppercase tracking-widest text-neutral-600 dark:text-neutral-400">
                Made By Jahangir
              </span>
            </div>
          )}
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center md:gap-4 lg:gap-12">
          <ul className="flex items-center md:gap-0.5 lg:gap-6">
            {navItems.map((item) => (
              <li key={item.id} className="relative">
                <a
                  href={item.href}
                  onClick={(e) => handleScrollTo(e, item.id)}
                  className={`group font-mono text-xs font-medium tracking-widest uppercase transition-all duration-300 px-4 py-2.5 md:px-2 lg:px-4 md:py-2 lg:py-2.5 rounded-lg relative ${
                    activeSection === item.id
                      ? 'text-emerald-500'
                      : 'text-neutral-500 dark:text-neutral-400 hover:text-black dark:hover:text-white'
                  }`}
                  data-cursor="hover"
                >
                  {/* Background green glow on hover */}
                  <span className="absolute inset-0 bg-emerald-500/0 group-hover:bg-emerald-500/[0.06] group-hover:shadow-[0_0_20px_rgba(16,185,129,0.18)] rounded-lg transition-all duration-300 -z-10" />
                  
                  {item.label}
                  {activeSection === item.id && (
                    <motion.span
                      layoutId="activeDot"
                      className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-emerald-500 rounded-full"
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                </a>
              </li>
            ))}
          </ul>

          {/* Premium Light/Dark Switch Widget */}
          <button
            onClick={toggleTheme}
            className="flex items-center justify-center p-2 rounded-full border border-neutral-200/50 dark:border-neutral-800/50 bg-neutral-100/50 dark:bg-neutral-900/50 hover:bg-neutral-200/50 dark:hover:bg-neutral-800/50 transition-colors duration-300"
            data-cursor="hover"
            title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
          >
            <div className="relative w-6 h-6 flex items-center justify-center overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                {theme === 'dark' ? (
                  <motion.div
                    key="sun"
                    initial={{ y: 20, rotate: 45, opacity: 0 }}
                    animate={{ y: 0, rotate: 0, opacity: 1 }}
                    exit={{ y: -20, rotate: -45, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                  >
                    <Sun className="w-4 h-4 text-emerald-500" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="moon"
                    initial={{ y: 20, rotate: -45, opacity: 0 }}
                    animate={{ y: 0, rotate: 0, opacity: 1 }}
                    exit={{ y: -20, rotate: 45, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                  >
                    <Moon className="w-4 h-4 text-emerald-600" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </button>
        </nav>

        {/* Mobile menu trigger */}
        <div className="flex items-center gap-4 md:hidden">
          {/* Light/Dark Toggle for Mobile */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full border border-neutral-200/50 dark:border-neutral-800/50 bg-neutral-100/50 dark:bg-neutral-900/50 hover:bg-neutral-200/50 dark:hover:bg-neutral-800/50 transition-colors duration-300"
            data-cursor="hover"
          >
            {theme === 'dark' ? (
              <Sun className="w-4 h-4 text-emerald-500" />
            ) : (
              <Moon className="w-4 h-4 text-emerald-600" />
            )}
          </button>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-neutral-800 dark:text-neutral-100 bg-neutral-100/50 dark:bg-neutral-900/50 rounded-full border border-neutral-200/50 dark:border-neutral-800/50 transition-colors"
            data-cursor="hover"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="md:hidden w-full bg-emerald-50/80 dark:bg-emerald-950/20 backdrop-blur-xl border-b border-emerald-500/20 dark:border-emerald-500/15 px-6 py-6 overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.1),_0_15px_30px_rgba(16,185,129,0.06)] relative z-50"
          >
            <ul className="flex flex-col gap-4 relative z-50">
              {navItems.map((item) => (
                <li key={item.id} className="relative z-50">
                  <a
                    href={item.href}
                    onClick={(e) => handleScrollTo(e, item.id)}
                    data-cursor="hover"
                    className={`font-mono text-[11px] font-bold tracking-widest uppercase block py-2 transition-all duration-300 pointer-events-auto cursor-pointer relative z-50 ${
                      activeSection === item.id
                        ? 'text-emerald-500 drop-shadow-[0_0_8px_rgba(16,185,129,0.3)]'
                        : 'text-neutral-500 dark:text-neutral-400 hover:text-black dark:hover:text-white hover:translate-x-1'
                    }`}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
